import { Check, ChevronDown, ExternalLink, ListChecks } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";

import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Spinner } from "@/components/ui/spinner";
import { ChatProse } from "@/features/agents/refs";
import { usePlanActions } from "@/features/agents/artifacts/PlanActionsContext";
import { cn } from "@/lib/utils";

import { CaseAgentAPI } from "../api/caseAgentApi";
import {
  useCaseIdeOptional,
  type CaseIdeContextValue
} from "../../context/CaseIdeContext";
import { PLAN_STATUS_LABEL, planFromToolOutput } from "../../plan/model";
import { PlanTodos } from "../../plan/PlanTodos";
import { PLAN_ACCENT_TEXT, PLAN_BUILD_BUTTON } from "../../plan/theme";
import type { CaseAgentPlan, PlanStatus } from "../../types";

import type { AnyToolPart } from "./ToolCard";

type Props = {
  part: AnyToolPart;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const str = (v: unknown, fallback = ""): string =>
  typeof v === "string" ? v : fallback;

const StatusMark: FC<{ status: PlanStatus }> = ({ status }) => (
  <span
    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
    data-testid="plan-card-status"
  >
    {status === "building" ? (
      <Spinner className={cn("size-3", PLAN_ACCENT_TEXT)} />
    ) : status === "complete" ? (
      <Check className="size-3" strokeWidth={2.5} />
    ) : null}
    {PLAN_STATUS_LABEL[status]}
  </span>
);

/**
 * The plan as it stands now. The tool output is a snapshot from when the plan
 * was written; in the IDE the card follows the stored plan, so its status and
 * to-dos move while the plan builds.
 */
const useLivePlan = (
  ide: CaseIdeContextValue | null,
  snapshot: CaseAgentPlan | null
): CaseAgentPlan | null => {
  const [live, setLive] = useState<CaseAgentPlan | null>(null);
  const planId = snapshot?.id;
  const individualId = ide?.individualId;
  const onInvalidate = ide?.onInvalidate;
  useEffect(() => {
    if (!planId || !individualId || !onInvalidate) return undefined;
    let alive = true;
    const load = () =>
      CaseAgentAPI.getPlan(individualId, planId).then(
        (next) => {
          if (alive) setLive(next);
        },
        () => undefined
      );
    void load();
    const off = onInvalidate((targets) => {
      if (targets.includes("plans")) void load();
    });
    return () => {
      alive = false;
      off();
    };
  }, [planId, individualId, onInvalidate]);
  return live && live.id === planId ? live : snapshot;
};

/**
 * A `create_plan` / `update_plan` call as Cursor shows a plan in the chat: a
 * quiet card with the plan's title and overview, its to-dos, and a footer
 * with the status and the Open / Build actions. The plan's body lives in the
 * editor, so it only shows here while the tool is still writing it, or when
 * there is nowhere to open it.
 */
export const PlanCard: FC<Props> = ({ part }) => {
  // In the Case IDE the plan opens as an editor tab; in an agent workspace it
  // opens in the artifact panel. Elsewhere (the side panel) there is nowhere
  // to show it, so the actions hide and the body stays in the card.
  const ide = useCaseIdeOptional();
  const actions = usePlanActions();
  const openPlan = ide
    ? (id: string, title: string) => ide.open({ kind: "plan", id, title })
    : actions?.openPlan ?? null;
  const requestPlanBuild = ide?.requestPlanBuild ?? actions?.buildPlan ?? null;
  const streaming = part.state === "input-streaming";
  const snapshot = useMemo(
    () =>
      part.state === "output-available"
        ? planFromToolOutput(part.output)
        : null,
    [part]
  );
  const plan = useLivePlan(ide, snapshot);
  const input = isRecord(part.input) ? part.input : {};
  const title = plan?.title ?? str(input.title, "Plan");
  const overview = plan?.overview ?? str(input.overview);
  const markdown = plan?.markdown ?? str(input.markdown);
  const todos = plan?.todos ?? [];
  const canBuild = Boolean(plan && plan.status === "draft");
  const [open, setOpen] = useState(true);
  const showBody = Boolean(markdown) && (!plan || !openPlan);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border border-line-2 bg-background text-sm text-foreground"
      data-testid="plan-card"
      data-status={plan?.status}
    >
      <div className="flex items-start gap-2.5 px-3 pt-2.5 pb-2">
        <ListChecks
          className={cn("mt-px size-4 shrink-0", PLAN_ACCENT_TEXT)}
          aria-hidden
        />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center gap-2">
            {plan && openPlan ? (
              <button
                type="button"
                className="min-w-0 truncate text-left text-base font-medium text-foreground hover:underline"
                onClick={() => openPlan(plan.id, plan.title)}
              >
                {title}
              </button>
            ) : (
              <span className="min-w-0 truncate text-base font-medium text-foreground">
                {streaming ? <Shimmer>{title}</Shimmer> : title}
              </span>
            )}
            {streaming ? (
              <span className="shrink-0 text-xs text-muted-foreground">
                <Shimmer>Writing the plan…</Shimmer>
              </span>
            ) : null}
          </div>
          {overview ? (
            <p className="text-sm leading-[1.5] text-muted-foreground">
              {streaming ? <Shimmer>{overview}</Shimmer> : overview}
            </p>
          ) : null}
        </div>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            className="-mr-1 size-6 shrink-0 text-muted-foreground"
            aria-label={open ? "Collapse plan" : "Expand plan"}
          >
            <ChevronDown
              className={cn(
                "size-3.5 transition-transform",
                !open && "-rotate-90"
              )}
            />
          </Button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <div className="space-y-2 px-3 pb-2.5">
          {todos.length ? (
            <PlanTodos todos={todos} size="sm" className="bg-transparent" />
          ) : null}
          {showBody ? (
            <ChatProse
              streaming={streaming}
              className="max-h-72 overflow-auto rounded-md bg-fill-1 p-2.5 text-sm"
            >
              {markdown}
            </ChatProse>
          ) : null}
        </div>
      </CollapsibleContent>
      {plan ? (
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 border-t border-line-2 px-3 py-2">
          <StatusMark status={plan.status} />
          <div className="ml-auto flex items-center gap-1.5">
            {openPlan ? (
              <Button
                variant="ghost"
                size="xs"
                className="h-6 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => openPlan(plan.id, plan.title)}
              >
                Open plan <ExternalLink className="size-3" />
              </Button>
            ) : null}
            {canBuild && requestPlanBuild ? (
              <Button
                variant="primary"
                size="xs"
                className={cn(
                  "h-6 px-2.5 text-xs font-medium",
                  PLAN_BUILD_BUTTON
                )}
                onClick={() => requestPlanBuild(plan.id)}
              >
                Build
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </Collapsible>
  );
};
